import type {
  AgentExperimentListOutput,
  AgentExperimentOutput,
  CreateAgentExperimentInput,
  DeleteAgentExperimentOutput,
  EndAgentExperimentInput,
  LaunchAgentExperimentInput,
  ShipAgentExperimentVariantInput,
  UpdateAgentExperimentInput,
} from "schemas/generated/agentExperiment";
import type { RequestFn } from "./request";

function experimentPath(experimentId: string) {
  return `/api/v1/agent-experiments/${encodeURIComponent(experimentId)}`;
}

/** Experiments on agent sites (`/api/v1/agent-experiments`). */
export function createAgentExperimentsClient({
  request,
}: {
  request: RequestFn;
}) {
  return {
    /** Newest first: one site's experiments, or every site's in a project. */
    find: (input: { projectId: string } | { siteId: string }) => {
      return request<AgentExperimentListOutput>(
        "projectId" in input
          ? {
              method: "GET",
              path: "/api/v1/agent-experiments/by-project",
              query: { projectId: input.projectId },
            }
          : {
              method: "GET",
              path: "/api/v1/agent-experiments",
              query: { siteId: input.siteId },
            },
      );
    },
    create: (input: CreateAgentExperimentInput) => {
      return request<AgentExperimentOutput>({
        method: "POST",
        path: "/api/v1/agent-experiments",
        body: input,
      });
    },
    /** Variants and metrics change only while the experiment is a draft. */
    update: ({
      experimentId,
      ...input
    }: UpdateAgentExperimentInput & { experimentId: string }) => {
      return request<AgentExperimentOutput>({
        method: "PATCH",
        path: experimentPath(experimentId),
        body: input,
      });
    },
    launch: ({
      experimentId,
      ...input
    }: LaunchAgentExperimentInput & { experimentId: string }) => {
      return request<AgentExperimentOutput>({
        method: "POST",
        path: `${experimentPath(experimentId)}/launch`,
        body: input,
      });
    },
    end: ({
      experimentId,
      ...input
    }: EndAgentExperimentInput & { experimentId: string }) => {
      return request<AgentExperimentOutput>({
        method: "POST",
        path: `${experimentPath(experimentId)}/end`,
        body: input,
      });
    },
    shipVariant: ({
      experimentId,
      ...input
    }: ShipAgentExperimentVariantInput & { experimentId: string }) => {
      return request<AgentExperimentOutput>({
        method: "POST",
        path: `${experimentPath(experimentId)}/ship-variant`,
        body: input,
      });
    },
    delete: ({
      experimentId,
      siteId,
    }: {
      experimentId: string;
      siteId: string;
    }) => {
      return request<DeleteAgentExperimentOutput>({
        method: "DELETE",
        path: experimentPath(experimentId),
        query: { siteId },
      });
    },
  };
}

export type AgentExperimentsClient = ReturnType<
  typeof createAgentExperimentsClient
>;
